import { ReactElement, ReactNode, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Cookie from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from '@Calix-Commerce/design-system/components';

import { authenticationState, loadingState } from 'state/atoms';
import { USER_REQUEST_PATH_COOKIE } from 'utils/constants';
import { useExternalRedirect, useNotifyError, useUserPermissions } from 'utils/hooks';

import { notAuthenticatedConfig } from './modalConfigs';
import { useSessionController } from './useSessionController';
import { useAccountLoad } from './useAccountLoad';
import { AuthenticationState } from 'types';
import { storageSelectedAccountSelector } from 'state/selectors';
import { NoAccessModal } from './NoAccessModalContent';
import { useMutation } from '@apollo/client';
import { SEND_CONTACT_MESSAGE } from 'connectors/mutations';

const renderModal = ({
  isAuthenticated,
  eCommerceAccess,
  commerceComplete,
  handleAccessRequest,
}: {
  commerceComplete: boolean;
  handleAccessRequest: () => Promise<void>;
} & AuthenticationState) => {
  if (!isAuthenticated) {
    return <Modal isOpen position="top" {...notAuthenticatedConfig} />;
  }

  const hasEmptyAccess = !eCommerceAccess.length || !commerceComplete;

  return (
    <NoAccessModal handleSendRequestMessage={handleAccessRequest} emptyAccess={hasEmptyAccess} />
  );
};

const AuthOverlay = ({ children }: { children: ReactNode }): ReactElement | null => {
  const navigate = useNavigate();
  const authentication = useRecoilValue(authenticationState);
  const selectedAccount = useRecoilValue(storageSelectedAccountSelector);
  const setGlobalLoading = useSetRecoilState(loadingState);
  const notifyError = useNotifyError({ fallbackMessage: 'Unable to send message.' });
  const [sendContactMessage, { loading: messageLoading }] = useMutation(SEND_CONTACT_MESSAGE);

  const { pathname } = useLocation();
  const { hasQuoteAccess } = useUserPermissions();
  const { eCommerceAccess } = authentication;

  useSessionController();

  const { loading: isLoadingAccount, commerceComplete } = useAccountLoad();
  const { redirectFromPermissions } = useExternalRedirect();

  const isAccountSearchRoute = pathname === '/accounts' || pathname === '/account-search';

  // Allow access to /accounts page even if the user has no Quote access
  const canAccessPage = (eCommerceAccess.length && isAccountSearchRoute) || hasQuoteAccess;

  useEffect(() => {
    Cookie.remove(USER_REQUEST_PATH_COOKIE);

    if (!canAccessPage) {
      // Tries to redirect the user to other apps based on their permissions
      redirectFromPermissions();
    } else if (!selectedAccount?.accountId && isAccountSearchRoute) {
      // Redirects to account search if user has no account selected
      navigate('/accounts', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset local storage if the user is not authenticated - commented and retained for revert/future use
  /* if (!authentication || !authentication.isAuthenticated) {
    localStorage.removeItem(CURRENT_QUOTE_LOCAL_STORAGE);
    localStorage.removeItem(CURRENT_ACCOUNT_LOCAL_STORAGE);
  } */

  useEffect(() => {
    setGlobalLoading(messageLoading);
  }, [messageLoading, setGlobalLoading]);

  const handleAccessRequest = async (): Promise<void> => {
    await sendContactMessage({
      variables: {
        messageInput: {
          accountId: authentication.endCustomerAccountId,
          recipientType: 'CustomerOperations',
          type: 'MyCalixStoreSupport',
          content: '*My Calix Store Quote Manager User Access Request*',
        },
      },
    }).catch((error: Error) => {
      notifyError(error);
      return Promise.reject(error);
    });
  };

  const renderApp = (): ReactElement => {
    return canAccessPage && commerceComplete ? (
      <>{children}</>
    ) : (
      renderModal({
        ...authentication,
        commerceComplete,
        handleAccessRequest,
      })
    );
  };

  return !isLoadingAccount ? renderApp() : null;
};

export { AuthOverlay };
